import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Trash2, Download, FolderOpen, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export default function PortalDocuments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState("Payroll");
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/portal/documents"); setData(data); }
    catch { toast.error("Could not load your documents."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (file) e.target.value = "";
    if (!file) return;
    setBusy(true); toast.message("Uploading & auto-filing with AI…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/portal/documents/upload", fd);
      await load(); setActive(data.document.category);
      toast.success(`Filed under ${data.document.category}.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); }
    finally { setBusy(false); }
  };

  const move = async (doc, category) => {
    if (category === doc.category) return;
    setData((d) => ({ ...d, documents: d.documents.map((x) => x.id === doc.id ? { ...x, category } : x) }));
    try { await api.put(`/portal/documents/${doc.id}/category`, { category }); await load(); toast.success(`Moved to ${category}.`); }
    catch { toast.error("Could not move."); await load(); }
  };

  const del = async (id) => {
    try { await api.delete(`/portal/documents/${id}`); await load(); toast.success("Removed."); }
    catch { toast.error("Could not remove."); }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/portal/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.mime || "application/octet-stream" }));
      const a = document.createElement("a"); a.href = url; a.download = doc.filename || "document";
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => window.URL.revokeObjectURL(url), 800);
    } catch (e) {
      if (e?.response?.status === 403) {
        try {
          await api.post("/portal/download-requests", { file_id: doc.id, label: doc.filename || "Document", resource_type: "document" });
          toast.success("Download requested — John or Kalib will review it shortly.");
        } catch { toast.error("Could not request approval."); }
        return;
      }
      toast.error("Download failed.");
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-[#1e3d3a]/60 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading your documents…</div>;
  if (!data) return null;
  const inCat = data.documents.filter((d) => d.category === active);

  return (
    <div data-testid="portal-documents" className="space-y-5">
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-[#1e3d3a]"><FolderOpen className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">Documents</h3></div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" className="hidden" data-testid="doc-upload-input" onChange={onUpload} accept=".csv,.txt,.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.docx" />
            <button data-testid="doc-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-xs font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50 transition-colors">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload & auto-file
            </button>
          </div>
        </div>
        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="doc-tabs">
          {data.categories.map((c) => (
            <button key={c} data-testid={`doc-tab-${c}`} onClick={() => setActive(c)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs border transition-colors ${active === c ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a]" : "border-[#1e3d3a]/12 text-[#1e3d3a]/55 hover:text-[#1e3d3a]/80"}`}>
              {c} <span className="tabular-nums opacity-60">{data.counts[c] || 0}</span>
            </button>
          ))}
        </div>
        {inCat.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {inCat.map((doc) => (
              <div key={doc.id} data-testid={`doc-card-${doc.id}`} className="rounded-xl border border-[#1e3d3a]/12 bg-[#faf8f2] p-4 flex items-center gap-3">
                <FileText className="h-5 w-5 text-[#1e3d3a]/40 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#1e3d3a] truncate">{doc.filename}</div>
                  <div className="text-[10px] text-[#1e3d3a]/45">{(doc.size / 1024).toFixed(0)} KB · {new Date(doc.uploaded_at).toLocaleDateString()}</div>
                </div>
                <select data-testid={`doc-move-${doc.id}`} value={doc.category} onChange={(e) => move(doc, e.target.value)}
                  title="Move to another category" className="rounded-lg border border-[#1e3d3a]/15 bg-white px-2 py-1 text-[10px] text-[#1e3d3a] focus:outline-none focus:ring-1 focus:ring-[#c9a961]">
                  {data.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button data-testid={`doc-download-${doc.id}`} onClick={() => download(doc)} className="p-1.5 text-[#1e3d3a]/45 hover:text-[#2f7d5b]" title="Download (needs approval)"><Download className="h-4 w-4" /></button>
                <button data-testid={`doc-del-${doc.id}`} onClick={() => del(doc.id)} className="p-1.5 text-[#1e3d3a]/45 hover:text-[#b23a48]"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-[#1e3d3a]/50 py-10 text-center">No files in <b>{active}</b> yet. Upload a document — the AI files it automatically, and you can move it with the dropdown.</p>}
      </div>
    </div>
  );
}
